import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  loadScenario,
  scenariosForTrack,
  scenarioFileFor,
  titleForFile,
  type CourseTrack,
} from "../../utils/scenarioForCall";
import { normalizePhrase, classifyScriptLines } from "../../utils/scriptMatch";
import CoursePicker from "../course/CoursePicker";
import { courseImgSrc } from "../course/courseImages";
import { hasMark, spaceCourseBlocks } from "../../utils/courseMarkdown";

// =============================================================================
// CourseScenarioPanel — the phone-setter script beside the call being reviewed
// =============================================================================
// Closer calls (track="closer") read the closer outbound course instead.
// Third column of the dial call-review master-detail (documented by /dialview).
// Shows the exact course scenario a phone setter SHOULD run for THIS lead, so a
// reviewer can read the transcript (middle) against the script (here) and answer
// "did they follow it?" without leaving the page.
//
// AUTO-SELECT: the file is chosen from the call's funnel-at-dial. The parent
// remounts this panel per selected call (key={call.id}), so each click resets to
// that call's scenario; the picker lets the reviewer switch by hand within a call.
//
// CONTENT: static markdown under public/course/phone-setter/ (copied from atlas
// resources/phone-setter-canvas2 by the course sync). Fetched once per file and
// cached in-module, so re-opening a scenario is instant and offline-safe. No DB,
// no Supabase — read-only reference. Images are not in the export yet (phase 2).
// =============================================================================

// In-module cache: file name → markdown text. Survives remounts within a session.
const cache = new Map<string, string>();

interface CourseScenarioPanelProps {
  /** Which course to read: "closer" → closer outbound docs, else phone setter.
   *  Hosts derive it with courseTrackForRole(caller role). REQUIRED. */
  track: CourseTrack;
  /** The selected call's funnel-at-dial (arrival event_type), for auto-select. */
  funnelAtDial: string | null | undefined;
  /** A teleport request from a clicked transcript highlight: switch to `file` and
   *  scroll to `phrase`. A fresh object (even same values) re-triggers the jump. */
  jumpTo?: { file: string; phrase: string } | null;
}

// react-markdown renderers — glass-styled, sized for a reading panel (bigger than
// the transcript-summary overrides). @tailwindcss/typography is not configured,
// so every element is normalized here or it renders at raw browser defaults.
// Real token classes only (token-12/token-16 are phantom → 16px fallback). Prose
// body = Inter 13 (token-13); doc title = PJS section (token-14s); section labels
// = Inter 11 upper (token-11). Generous vertical rhythm so scripts read like a doc.
const mdComponents: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  // Doc title (the scenario name).
  // word-spacing: PJS 900 + 0.06em tracking made the word gaps look gone ("SHOWANDTELL").
  h1: ({ children }) => <h3 className="token-14s text-glass mt-6 mb-2 first:mt-0 [word-spacing:0.22em]">{children}</h3>,
  // Section header (THE SITUATION / WHAT THIS DOCUMENT IS FOR).
  h2: ({ children }) => (
    <h4 className="token-11 uppercase tracking-[0.08em] text-glass-secondary mt-6 mb-2 pt-1">
      {children}
    </h4>
  ),
  // Sub-header (OPENER A / Registered, Didn't Attend).
  h3: ({ children }) => (
    <h5 className="token-13 font-semibold text-glass mt-4 mb-1.5">{children}</h5>
  ),
  h4: ({ children }) => (
    <h6 className="token-11 font-semibold uppercase tracking-wide text-glass-muted mt-3 mb-1">
      {children}
    </h6>
  ),
  p: ({ children }) => (
    <p className="token-13 text-glass-secondary leading-relaxed my-2.5">{children}</p>
  ),
  strong: ({ children }) => <strong className="font-semibold text-glass">{children}</strong>,
  // Course pictures: storage bucket once VITE_COURSE_IMG_BASE is set, else /course-img/.
  img: ({ src, alt, title }) => <img src={courseImgSrc(src)} alt={alt} title={title} />,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1.5 my-2.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1.5 my-2.5">{children}</ol>,
  // A "☐" / "✅" / "❌" item: the mark IS the marker, so no bullet; wrapped
  // lines hang under the text (courseMarkdown turns "- [ ]" into "- ☐").
  li: ({ children, node }) => (
    <li
      className={`token-13 text-glass-secondary leading-relaxed ${
        hasMark(node) ? "list-none -ml-5 pl-[1.35em] -indent-[1.35em]" : "pl-1"
      }`}
    >
      {children}
    </li>
  ),
  // Script container. formatScript() routes the SPOKEN script here, so this is
  // styled to say "read this out" — solid left accent bar (Slack-blockquote
  // style), faint accent tint, normal upright text — NOT the muted-italic look a
  // real pull-quote would get. Bold speaker labels + italic (stage) directions
  // come from the strong/em renderers below.
  blockquote: ({ children }) => (
    <blockquote className="border-l-[3px] border-ds-accent/70 bg-ds-accent/[0.05] rounded-r-md pl-4 pr-3 py-1.5 my-3">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-5 border-black/[0.08]" />,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-ds-accent hover:underline">
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="rounded bg-black/[0.05] px-1.5 py-0.5 token-13m text-glass">{children}</code>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-black/[0.08]">
      <table className="w-full border-collapse token-11">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-black/[0.03]">{children}</thead>,
  th: ({ children }) => (
    <th className="px-2.5 py-2 text-left font-semibold text-glass border-b border-black/[0.08] min-w-[6rem]">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-2.5 py-2 text-glass-secondary align-top border-b border-black/[0.05] min-w-[6rem]">
      {children}
    </td>
  ),
};

// Strip the leading "<!-- Canvas 2 tab: … -->" provenance comment so it never
// renders as a wall of text at the top of the panel.
const stripLeadComment = (md: string): string => md.replace(/^\s*<!--[\s\S]*?-->\s*/, "");

// =============================================================================
// formatScript — separate the SPOKEN SCRIPT from the COACHING, at render time
// =============================================================================
// A scenario doc interleaves two things a reviewer must tell apart instantly:
//   • SPOKEN SCRIPT — the words the setter says. Marked "YOU:" / "LEAD:", plus
//     the whole-line stage directions like "(Pause.)".
//   • COACHING — the why/how prose around it ("Why it works…", "Don't claim
//     you've spoken before", the "### …" explainer sections).
// We lift the spoken script into a Markdown blockquote so it renders as a
// Slack-style left accent bar ("this is what you say"), while the coaching stays
// as plain prose. Nothing is edited in the 38 source .md — they stay a clean
// mirror of atlas resources/phone-setter-canvas2. All of this is render-only.
//
// What counts as spoken script (dialogue labels incl. Voicemail:/Text:/NAME:,
// quote-dominant example lines, and their continuations) is decided by
// classifyScriptLines() in scriptMatch — the SAME rule the transcript-highlight
// uses, so the bar and the green highlight never disagree. Reference tables and
// coaching prose stay out of the bar. Nothing is edited in the 38 source .md.

const isSpeaker = (l: string): boolean => /^\s*(?:\*\*)?(?:YOU|LEAD):/.test(l);
const isHeader = (l: string): boolean => /^\s*#{1,6}\s/.test(l);
const isBlank = (l: string): boolean => /^\s*$/.test(l);
const isDirection = (l: string): boolean => /^\s*\(.*\)\s*$/.test(l);

// Bold ANY dialogue label; italicize a whole-line stage direction.
const LABEL_AT_START = /^(\s*)((?:YOU|LEAD|Voicemail|Voicenote|VM|Text|SMS):|[A-Z]{2,}:)/;
const styleScriptLine = (l: string): string => {
  if (LABEL_AT_START.test(l)) return l.replace(LABEL_AT_START, "$1**$2**");
  if (isDirection(l)) return l.replace(/^(\s*)(\(.*\))(\s*)$/, "$1*$2*$3");
  return l;
};

// Render a run of script lines as one blockquote, each turn on its own line
// (two-space Markdown hard break so they stack inside the bar).
const quoteScript = (lines: string[]): string =>
  lines
    .map((s, idx) => "> " + styleScriptLine(s) + (idx < lines.length - 1 ? "  " : ""))
    .join("\n");

// --- Table repair ----------------------------------------------------------
// The gdoc export drops the delimiter row every GFM table needs (the "| --- |
// --- |" under the header). Without it Markdown gives up and prints the raw
// pipes as one flattened paragraph. We put the delimiter back at render time and
// fence the table with blank lines so it parses. Source .md stay untouched.
const isTableRow = (l: string): boolean => /^\s*\|.*\|\s*$/.test(l);
const isDelimiterRow = (l: string): boolean => /^\s*\|(?:\s*:?-{2,}:?\s*\|)+\s*$/.test(l);
const colCount = (l: string): number =>
  l
    .trim()
    .replace(/^\|/, "")
    .replace(/\|\s*$/, "")
    .split("|").length;

const repairTables = (lines: string[]): string[] => {
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    const prevIsRow = i > 0 && isTableRow(lines[i - 1]);
    const nextIsRow = i + 1 < lines.length && isTableRow(lines[i + 1]);
    // A header row: a table row that starts a block and whose next row is NOT
    // already a delimiter. That is the one missing its "| --- |" line.
    if (isTableRow(l) && !prevIsRow && nextIsRow && !isDelimiterRow(lines[i + 1])) {
      const prev = out[out.length - 1];
      if (out.length && !isBlank(prev) && !isHeader(prev)) out.push(""); // blank before
      out.push(l);
      out.push("|" + " --- |".repeat(colCount(l)));
      continue;
    }
    out.push(l);
    // Blank after the last row of a repaired table, so following prose doesn't
    // get lazily pulled into it.
    const next = lines[i + 1];
    if (isTableRow(l) && !nextIsRow && next != null && !isBlank(next) && !isHeader(next))
      out.push("");
  }
  return out;
};

const formatScript = (mdRaw: string): string => {
  const md = stripLeadComment(mdRaw);
  // Explode any packed line ("YOU: a LEAD: b YOU: c") so each turn owns a line.
  // Only lines that START with a speaker are exploded, so a coaching line that
  // merely mentions "YOU:" mid-sentence is left untouched.
  const exploded: string[] = [];
  for (const raw of md.split("\n")) {
    if (isSpeaker(raw))
      for (const l of raw.replace(/[ \t]+(YOU:|LEAD:)/g, "\n$1").split("\n")) exploded.push(l);
    else exploded.push(raw);
  }
  // Put missing table delimiters back before classifying.
  const lines = repairTables(exploded);
  // Mark each line script/not with the shared rule, then blockquote every run of
  // consecutive script lines; everything else (headers, tables, coaching) emits
  // as-is.
  const script = classifyScriptLines(lines);
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    if (!script[i]) {
      out.push(lines[i]);
      i++;
      continue;
    }
    const run: string[] = [];
    while (i < lines.length && script[i]) {
      run.push(lines[i]);
      i++;
    }
    out.push("");
    out.push(quoteScript(run));
    out.push("");
  }
  return out.join("\n");
};

// Pull the "quoted" fragments out of a line (straight or curly quotes). Rescues
// the spoken questions that live inside reference-table rows, which
// classifyScriptLines marks non-script (they are table rows) but which the setter
// still says out loud (e.g. `| 1 | "And how long..." |`).
const quotedFragments = (l: string): string[] => l.replace(/[“”]/g, '"').match(/"[^"]+"/g) ?? [];

// =============================================================================
// scriptOnly — the SPOKEN LINES only, reasoning stripped out
// =============================================================================
// A second render mode for the panel. Keeps ONLY what a setter says out loud,
// plus the thin section/branch headers so the reviewer knows which opener he is
// reading. Everything else is dropped: coaching prose, "why it works", intro
// paragraphs, reference tables. A header is kept ONLY when a spoken line sits
// inside ITS OWN section (scoped by header level), so a purely-coaching section
// like "PART 1: WHAT TRIAGING IS" leaves no empty label behind. Uses the SAME
// classifier as formatScript (classifyScriptLines), so the two views never
// disagree about what counts as script. Render-only — source .md untouched.
const scriptOnly = (mdRaw: string): string => {
  const md = stripLeadComment(mdRaw);
  // Explode packed speaker lines, same as formatScript.
  const lines: string[] = [];
  for (const raw of md.split("\n")) {
    if (isSpeaker(raw))
      for (const l of raw.replace(/[ \t]+(YOU:|LEAD:)/g, "\n$1").split("\n")) lines.push(l);
    else lines.push(raw);
  }
  const script = classifyScriptLines(lines);
  // The spoken text a line contributes (empty array = drop the line).
  const spokenOf = (l: string, isScript: boolean): string[] => {
    if (isScript) return [l];
    if (isTableRow(l) || /^\s*([-*+]|\d+[.)])\s+/.test(l)) return quotedFragments(l); // a quoted line in a table row or bullet
    return [];
  };

  // Header STACK, scoped by level (# = 1 … ###### = 6). A header is only printed
  // once a spoken line lands inside its section; a header popped before that
  // (its whole section was coaching) is dropped. So a kept header always leads to
  // real script, and the parent chain above a script line stays for context.
  const headerLevel = (l: string): number => l.match(/^\s*(#{1,6})/)?.[1].length ?? 1;
  const stack: Array<{ level: number; text: string; emitted: boolean }> = [];
  const out: string[] = [];
  let run: string[] = [];
  const flushRun = () => {
    if (!run.length) return;
    out.push("", quoteScript(run), "");
    run = [];
  };
  const emitAncestors = () => {
    for (const h of stack)
      if (!h.emitted) {
        out.push("", h.text);
        h.emitted = true;
      }
  };
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (isHeader(l)) {
      flushRun();
      const level = headerLevel(l);
      while (stack.length && stack[stack.length - 1].level >= level) stack.pop();
      stack.push({ level, text: l, emitted: false });
      continue;
    }
    const spoken = spokenOf(l, script[i]);
    if (spoken.length) {
      if (!run.length) emitAncestors(); // print the header chain before a new bar
      run.push(...spoken);
    } else {
      flushRun(); // coaching / blank / non-quoted table ends any open run
    }
  }
  flushRun();
  return out.join("\n").replace(/\n{3,}/g, "\n\n");
};

// -----------------------------------------------------------------------------
// Exact-phrase teleport highlight. When a transcript span is clicked we mark the
// EXACT matched words in the course (not the whole block), using the CSS Custom
// Highlight API — it paints a Range with no DOM mutation, so it never fights the
// ReactMarkdown output. A block-flash fallback covers browsers without the API.
// -----------------------------------------------------------------------------
const HIGHLIGHT_NAME = "script-jump";
const HIGHLIGHT_STYLE_ID = "script-jump-highlight-style";
let _highlightClearTimer: number | null = null;

const ensureHighlightStyle = (): void => {
  if (document.getElementById(HIGHLIGHT_STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = HIGHLIGHT_STYLE_ID;
  s.textContent = `::highlight(${HIGHLIGHT_NAME}){background-color:rgba(16,185,129,0.45);border-radius:2px;color:inherit;}`;
  document.head.appendChild(s);
};

// A DOM Range over the phrase's words inside one scope element, spanning text
// nodes as needed. Exact token match (the course text is the phrase's own source).
const findRangeInScope = (scope: HTMLElement, tokens: string[]): Range | null => {
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  const words: Array<{ node: Text; start: number; end: number; norm: string }> = [];
  const wre = /[a-z0-9]+/gi;
  let node: Node | null;
  while ((node = walker.nextNode()) !== null) {
    const t = node as Text;
    let m: RegExpExecArray | null;
    wre.lastIndex = 0;
    while ((m = wre.exec(t.data)) !== null) {
      words.push({ node: t, start: m.index, end: m.index + m[0].length, norm: m[0].toLowerCase() });
    }
  }
  const k = tokens.length;
  for (let i = 0; i + k <= words.length; i++) {
    let ok = true;
    for (let j = 0; j < k; j++) {
      if (words[i + j].norm !== tokens[j]) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    const range = document.createRange();
    range.setStart(words[i].node, words[i].start);
    range.setEnd(words[i + k - 1].node, words[i + k - 1].end);
    return range;
  }
  return null;
};

// Find the phrase ONLY inside the script bars (blockquotes) and question-table
// cells — never coaching prose. Anchors only ever come from those, so this both
// finds the right spot and guarantees a click never lands on a comment.
const findPhraseRange = (root: HTMLElement, tokens: string[]): Range | null => {
  if (!tokens.length) return null;
  const scopes = Array.from(root.querySelectorAll("blockquote, td, th")) as HTMLElement[];
  for (const scope of scopes.length ? scopes : [root]) {
    const r = findRangeInScope(scope, tokens);
    if (r) return r;
  }
  return null;
};

const CourseScenarioPanel: React.FC<CourseScenarioPanelProps> = ({ track, funnelAtDial, jumpTo }) => {
  // Auto-selected file for this call; the picker overrides it until the parent
  // remounts on the next call selection (key={call.id}).
  const autoFile = useMemo(() => scenarioFileFor(track, funnelAtDial), [track, funnelAtDial]);
  const [file, setFile] = useState<string>(autoFile);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Derive content from the module cache during render, so switching to an
  // already-fetched scenario needs no setState-in-effect (repo lint bans it).
  // The effect only does the async fetch; it bumps `nonce` in its callback (which
  // is allowed) to re-render once the cache is populated.
  const [, setNonce] = useState(0);
  // Error is keyed by file so switching scenarios clears a stale error for free.
  const [errFor, setErrFor] = useState<{ file: string; msg: string } | null>(null);
  // Full doc vs script-only (spoken lines + thin headers, coaching stripped).
  const [scriptView, setScriptView] = useState(false);
  const setView = (v: boolean) => {
    setScriptView(v);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const text = cache.get(file) ?? null;
  const error = errFor?.file === file ? errFor.msg : null;
  const loading = text == null && error == null;

  useEffect(() => {
    // Already cached (or errored on this exact file) — just reset scroll, no fetch.
    if (cache.has(file)) {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      return;
    }
    let cancelled = false;
    loadScenario(file)
      .then((body) => {
        if (cancelled) return;
        cache.set(file, body);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        setNonce((n) => n + 1);
      })
      .catch((e) => {
        if (cancelled) return;
        setErrFor({ file, msg: e instanceof Error ? e.message : "Failed to load scenario" });
      });

    return () => {
      cancelled = true;
    };
  }, [file]);

  // Teleport: a clicked transcript highlight asks us to show `jumpTo.file` and
  // scroll to `jumpTo.phrase`. Switch the file first; once its text has loaded
  // and rendered, find the phrase in the body, scroll it in, and flash it green.
  useEffect(() => {
    if (!jumpTo) return;
    // Deferred to a frame so the file switch (setState) is not synchronous in the
    // effect body (repo lint), and so scrolling happens after layout is ready.
    const raf = requestAnimationFrame(() => {
      if (jumpTo.file && jumpTo.file !== file) {
        setFile(jumpTo.file); // effect re-runs when the new file's text lands (text dep)
        return;
      }
      if (text == null) return; // wait for the fetch
      const body = scrollRef.current;
      if (!body) return;
      const tokens = normalizePhrase(jumpTo.phrase).split(" ").filter(Boolean);
      const range = findPhraseRange(body, tokens);
      if (!range) return;

      // Center the EXACT phrase in the scroll container (not the whole block).
      const rect = range.getBoundingClientRect();
      const contRect = body.getBoundingClientRect();
      const top =
        rect.top - contRect.top + body.scrollTop - body.clientHeight / 2 + rect.height / 2;
      body.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

      // Paint just the matched words green for a beat.
      ensureHighlightStyle();
      const Ctor = (window as unknown as { Highlight?: new (...r: Range[]) => unknown }).Highlight;
      const store = (
        CSS as unknown as {
          highlights?: { set(k: string, v: unknown): void; delete(k: string): void };
        }
      ).highlights;
      if (_highlightClearTimer != null) window.clearTimeout(_highlightClearTimer);
      if (Ctor && store) {
        store.set(HIGHLIGHT_NAME, new Ctor(range));
        _highlightClearTimer = window.setTimeout(() => store.delete(HIGHLIGHT_NAME), 2400);
      } else {
        // Fallback (no Highlight API): flash the phrase's block element.
        const el =
          range.startContainer.parentElement?.closest("p,li,td,th,blockquote,h1,h2,h3,h4,h5,h6") ??
          range.startContainer.parentElement;
        if (el instanceof HTMLElement) {
          const prev = el.style.backgroundColor;
          el.style.transition = "background-color 400ms ease";
          el.style.backgroundColor = "rgba(16,185,129,0.22)";
          _highlightClearTimer = window.setTimeout(() => {
            el.style.backgroundColor = prev;
          }, 1500);
        }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [jumpTo, file, text]);

  const isAuto = file === autoFile;

  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid="course-scenario-panel"
      data-course-track={track}
    >
      {/* Header — label + view toggle on ONE row, picker below. Compact on
          purpose: the toggle used to own a third row; folding it up onto the
          label row protects vertical space for the script body. */}
      <div className="px-5 pt-3 pb-2 border-b border-black/[0.08] shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="token-10u text-glass-muted truncate" data-testid="course-scenario-label">
              {track === "closer" ? "Course · Closer Outbound" : "Course · Phone Setter"}
            </span>
            {isAuto && (
              <span
                className="token-10 px-1.5 py-0.5 rounded bg-ds-accent/[0.1] text-ds-accent shrink-0"
                title={
                  track === "closer"
                    ? "Picked because a closer made this call"
                    : "Picked from this call's funnel"
                }
              >
                auto
              </span>
            )}
          </div>
          {/* View toggle: Full doc vs script-only (spoken lines, no coaching). */}
          <div
            className="flex shrink-0 rounded-md bg-black/[0.04] p-0.5 token-11"
            data-testid="course-view-toggle"
            role="tablist"
            aria-label="Course view"
          >
            <button
              type="button"
              role="tab"
              aria-selected={!scriptView}
              onClick={() => setView(false)}
              data-testid="course-view-full"
              className={`anim-tap rounded px-2.5 py-0.5 transition-colors ${
                !scriptView
                  ? "bg-ds-accent/[0.12] text-ds-accent font-medium"
                  : "text-glass-muted hover:text-glass"
              }`}
            >
              Full
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scriptView}
              onClick={() => setView(true)}
              data-testid="course-view-script"
              className={`anim-tap rounded px-2.5 py-0.5 transition-colors ${
                scriptView
                  ? "bg-ds-accent/[0.12] text-ds-accent font-medium"
                  : "text-glass-muted hover:text-glass"
              }`}
            >
              Script only
            </button>
          </div>
        </div>
        {/* Grouped hover picker: /triangle on the group rail, /animate on open. */}
        <CoursePicker
          items={scenariosForTrack(track)}
          value={file}
          onChange={setFile}
          testId="course-scenario-picker"
          ariaLabel={track === "closer" ? "Pick a closer course doc" : "Pick a phone setter course doc"}
        />
      </div>

      {/* Body: the script, full remaining height, own scroll */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto px-5 py-4"
        data-testid="course-scenario-body"
      >
        {loading && (
          <div className="flex items-center gap-2 py-8 justify-center text-glass-muted token-13">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            Loading {titleForFile(file)}…
          </div>
        )}
        {error && !loading && (
          <p className="token-13 text-glass-secondary py-6 text-center">
            Could not load this scenario ({error}).
          </p>
        )}
        {!loading && !error && text != null && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {spaceCourseBlocks(scriptView ? scriptOnly(text) : formatScript(text))}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
};

export default CourseScenarioPanel;
